import React, { useEffect, useState } from 'react';
import Row from '@amzn/meridian/row';
import Textarea from '@amzn/meridian/textarea';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';

import AtAcrossZoneBulkUpload from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneBulkUpload';
import AtAcrossZoneDiffTable from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneDiffTable';
import { InputAsinsValidationUtils, getAsinValidationMessage } from '@components/agile/utils/InputAsinsValidationUtils';
import Button from '@amzn/meridian/button';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import AtAcrossZoneRecommedationTable from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneRecommedationTable';
import { AgileBatchRecommedationInput } from '@components/agile/types/AgileRecommendationTypes';
import { getActionPublisherForProgram, publishKatalMetric } from 'src/metrics/publishers';
import {
  METHOD_NAME_BULK_UPLOAD,
  METRIC_NAME_GENERATE_DIFF,
  METRIC_NAME_GO_TO_BULK_UPLOAD,
  MetricType,
} from 'src/constants/metricConstants';
import usePromoteDraftZone from 'src/store/hooks/usePromoteDraftZone';
import getDateInUTC from '@components/agile/utils/GetDateUtils';
import { useNavigate } from 'react-router-dom';
import AgileUploadRow from '../../types/AgileUploadRow';
import AgileURLParameterTypes from '../../types/AgileURLParameterTypes';
import AtAcrossZoneModals from './AtAcrossZoneModals';

export interface AtAcrossZoneMainProps {
  listOfMacroplanogram: AgileMacroplanogram[] | undefined;
  swapTime: number;
  includeDraft: boolean;
}
const AtAcrossZoneMain = (props: AtAcrossZoneMainProps) => {
  const { listOfMacroplanogram, swapTime, includeDraft } = props;
  const [inputAsins, setInputAsins] = useState<string[]>([]);
  const [textAreaInput, setTextAreaInput] = useState<string>('');
  const [enableTextArea, setEnableTextArea] = useState<boolean>(false);
  const [textAreaError, setTextAreaError] = useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(true);
  const [disableQuickPromoteButton, setDisableQuickPromoteButton] = useState<boolean>(true);
  const [inputTableFlag, setInputTableFlag] = useState(false);
  const [alertFlag, setAlertFlag] = useState<boolean>(false);
  const [zoneNotFoundAlertFlag, setZoneNotFoundAlertFlag] = useState<boolean>(false);
  const [recommendationInput, setRecommendationInput] = useState<AgileBatchRecommedationInput[]>([]);
  const [updatedRecommendInputWithDiffData, setUpdatedRecommendInputWithDiffData] = useState<
    AgileBatchRecommedationInput[]
  >([]);
  const [recommendationFlag, setRecommendationFlag] = useState<boolean>(false);
  const [diffTableData, setDiffTableData] = useState<AgileUploadRow[]>([]);
  const [macroplanogramToFacingUnitsMap, setMacroplanogramToFacingUnitsMap] = useState<{
    [macroplanogramId: string]: number;
  }>({});
  const [successful, setSuccessful] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [promoteDraftErrorMsg, setPromoteDraftErrorMsg] = useState<string | undefined>(undefined);
  const { mutateAsync: promoteDraftZone } = usePromoteDraftZone();
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [minDraftStartTime, setMinDraftStartTime] = useState<number>(0);
  const [agreementCheckBox, setAgreementCheckBox] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    publishKatalMetric(
      getActionPublisherForProgram(METHOD_NAME_BULK_UPLOAD),
      METRIC_NAME_GO_TO_BULK_UPLOAD,
      1,
      MetricType.Counter,
    );
  }, []);

  useEffect(() => {
    const asinList = Array.from(new Set(textAreaInput.split(/[,\s]+/)));
    const asinValidationFlag = !InputAsinsValidationUtils(asinList);
    if (!asinValidationFlag) setInputAsins(asinList);
    setTextAreaError(getAsinValidationMessage(asinList));
    setDisableButton(asinValidationFlag);
    setRecommendationFlag(false);
    setInputTableFlag(false);
    setDisableQuickPromoteButton(true);
  }, [textAreaInput]);

  useEffect(() => {
    const isListOfMacroplanogramValid = listOfMacroplanogram !== undefined && listOfMacroplanogram !== null;
    const isListOfMacroplanogramEmpty = Array.isArray(listOfMacroplanogram) && listOfMacroplanogram.length === 0;
    setAlertFlag(!isListOfMacroplanogramValid);
    setInputTableFlag(false);
    setZoneNotFoundAlertFlag(isListOfMacroplanogramEmpty);
    setRecommendationFlag(false);
    setDisableQuickPromoteButton(true);
  }, [listOfMacroplanogram]);

  const onClick = () => {
    setInputTableFlag(false);
    setRecommendationFlag(false);
    const isListOfMacroplanogramValid = listOfMacroplanogram !== undefined && listOfMacroplanogram !== null;
    setAlertFlag(!isListOfMacroplanogramValid);
    publishKatalMetric(
      getActionPublisherForProgram(METHOD_NAME_BULK_UPLOAD),
      METRIC_NAME_GENERATE_DIFF,
      1,
      MetricType.Counter,
    );
    setTimeout(() => {
      setInputTableFlag(isListOfMacroplanogramValid);
    }, 500);
  };
  const onClose = () => {
    setSuccessful(false);
    setOpenConfirmationModal(false);
    navigate('/zoneCreation', {
      state: {
        storeId: listOfMacroplanogram?.[0]?.storeId || '',
        date: getDateInUTC(minDraftStartTime).toString() || '',
      } as AgileURLParameterTypes,
    });
  };
  const getMinimumDraftStartTime = (macroplanograms: AgileMacroplanogram[] | undefined): number => {
    if (!macroplanograms || macroplanograms.length === 0) {
      return 0;
    }

    const draftMacroplanograms = macroplanograms.filter((macro) => macro.isDraft);
    if (draftMacroplanograms.length === 0) {
      return 0;
    }

    const startTimes = draftMacroplanograms.map((macro) => parseInt(macro.startTime, 10));
    return startTimes.length > 0 ? Math.min(...startTimes) : 0;
  };
  const onClickQuickPromote = () => {
    const minStartTime = getMinimumDraftStartTime(listOfMacroplanogram);
    setMinDraftStartTime(minStartTime);
    setOpenConfirmationModal(true);
  };
  const handleConfirmPromotion = async () => {
    try {
      const resp = await promoteDraftZone({
        input: {
          storeId: listOfMacroplanogram?.[0]?.storeId || ' ',
          timestamp: minDraftStartTime,
        },
      });

      if (resp.promoteDraftZone.error == null) {
        setSuccessful(true);
        setOpenConfirmationModal(false);
      } else {
        setPromoteDraftErrorMsg(resp.promoteDraftZone.error.errorMessage);
        setOpenErrorModal(true);
        setOpenConfirmationModal(false);
      }
    } catch (error) {
      setPromoteDraftErrorMsg(
        error instanceof Error ? error.message : 'An unexpected error occurred while promoting the draft zone.',
      );
      setOpenErrorModal(true);
      setOpenConfirmationModal(false);
    }
  };

  return (
    <>
      {alertFlag && (
        <Alert type="error" onClose={() => setAlertFlag(false)}>
          Please select a store and a live date before continuing.
        </Alert>
      )}
      {zoneNotFoundAlertFlag && (
        <Alert type="error" onClose={() => setZoneNotFoundAlertFlag(false)}>
          The selected store does not contain any zones for the chosen date. Please create zones before proceeding.
        </Alert>
      )}
      <Column alignmentHorizontal="center" spacingInset="200">
        <Row alignmentHorizontal="center">
          <Textarea
            value={textAreaInput}
            onChange={setTextAreaInput}
            placeholder="Enter a list of ASINs that you would like to add across zones"
            error={textAreaError}
            resize="auto"
            rows={5}
            disabled={enableTextArea}
            errorMessage={textAreaError && !enableTextArea ? 'Invalid List of Asins' : null}
            helperText="For multiple ASINs use commas (,) eg: B00XXXXXFW, B00XXXXXF6"
            width={500}
          />
          <Column alignmentHorizontal="center" spacingInset="200">
            <AtAcrossZoneBulkUpload
              setEnableTextArea={setEnableTextArea}
              setInputAsins={setInputAsins}
              setDisableButton={setDisableButton}
              setInputTableFlag={setInputTableFlag}
              setRecommendationFlag={setRecommendationFlag}
              setTextAreaInput={setTextAreaInput}
            />
          </Column>
        </Row>
      </Column>

      <Row width="100%" alignmentHorizontal="center" spacingInset="300" backgroundColor="subdued">
        <Button disabled={disableButton} onClick={onClick}>
          Next
        </Button>
        {includeDraft && (
          <Button disabled={disableQuickPromoteButton} onClick={onClickQuickPromote}>
            Promote Draft To Live
          </Button>
        )}
      </Row>
      {inputTableFlag && (
        <AtAcrossZoneDiffTable
          listOfMacroplanogram={listOfMacroplanogram}
          swapTime={swapTime}
          inputAsins={inputAsins}
          setRecommendationInput={setRecommendationInput}
          setRecommendationFlag={setRecommendationFlag}
          setDiffTableData={setDiffTableData}
          setMacroplanogramToFacingUnitsMap={setMacroplanogramToFacingUnitsMap}
          setUpdatedRecommendInputWithDiffData={setUpdatedRecommendInputWithDiffData}
          includeDraft={includeDraft}
          setDisableQuickPromoteButton={setDisableQuickPromoteButton}
        />
      )}
      {recommendationFlag && (
        <AtAcrossZoneRecommedationTable
          recommendationInput={recommendationInput}
          swapTime={swapTime}
          listOfMacroplanogram={listOfMacroplanogram}
          diffTableData={diffTableData}
          macroplanogramToFacingUnitsMap={macroplanogramToFacingUnitsMap}
          setInputTableFlag={setInputTableFlag}
          setRecommendationFlag={setRecommendationFlag}
          updatedRecommendInputWithDiffData={updatedRecommendInputWithDiffData}
          includeDraft={includeDraft}
        />
      )}
      {/* Confirmation Modal */}
      <AtAcrossZoneModals
        openConfirmationModal={openConfirmationModal}
        openErrorModal={openErrorModal}
        successful={successful}
        agreementCheckBox={agreementCheckBox}
        minDraftStartTime={minDraftStartTime}
        inputAsins={inputAsins}
        promoteDraftErrorMsg={promoteDraftErrorMsg}
        setOpenConfirmationModal={setOpenConfirmationModal}
        setOpenErrorModal={setOpenErrorModal}
        setAgreementCheckBox={setAgreementCheckBox}
        handleConfirmPromotion={handleConfirmPromotion}
        onClose={onClose}
      />
    </>
  );
};

export default AtAcrossZoneMain;
